"use client";

import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import type { KeyboardEvent, RefAttributes } from "react";

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const OPEN_KEYS = ["Enter", " "];

// Radix opens the menu on keyboard activation but leaves focus on the trigger
// until ArrowDown is pressed; move focus into the overlay so that isn't needed.
const focusOverlay = (contentId: string, attempt = 0) => {
	const content = document.getElementById(contentId);
	const first = content?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

	if (first) {
		first.focus();
		return;
	}

	// Content mounts into the viewport a frame later; retry briefly.
	if (attempt < 5) {
		requestAnimationFrame(() => focusOverlay(contentId, attempt + 1));
	}
};

export const NavMenuTrigger = (
	props: NavigationMenu.NavigationMenuTriggerProps & RefAttributes<HTMLButtonElement>,
) => {
	const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		props.onKeyDown?.(event);

		if (!OPEN_KEYS.includes(event.key)) return;
		// Only when about to open; otherwise the keypress closes the menu.
		if (event.currentTarget.getAttribute("data-state") !== "closed") return;

		const contentId = event.currentTarget.getAttribute("aria-controls");
		if (contentId) {
			requestAnimationFrame(() => focusOverlay(contentId));
		}
	};

	return <NavigationMenu.Trigger {...props} onKeyDown={handleKeyDown} />;
};
